import React, { useState, useEffect } from 'react';
import { FileText, Upload, Trash2, Download, RefreshCw, Check, X, FileSpreadsheet, FileImage, FileVideo, FileAudio, FileCode, File, Pencil, Eye } from 'lucide-react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import FileViewerModal from './FileViewerModal';
import './ReferencedFilesPanel.css';

// Function to get file icon based on file extension
const getFileIcon = (filename) => {
  if (!filename) return FileText;
  
  const extension = filename.split('.').pop()?.toLowerCase();
  
  // PDF files
  if (extension === 'pdf') {
    return FileText; // Will style with red color
  }
  
  // Excel/Spreadsheet files
  if (['xls', 'xlsx', 'xlsm', 'xlsb', 'csv'].includes(extension)) {
    return FileSpreadsheet;
  }
  
  // Word/Document files
  if (['doc', 'docx', 'docm', 'odt', 'rtf'].includes(extension)) {
    return FileText; // Will style with blue color
  }
  
  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension)) {
    return FileImage;
  }
  
  // Video files
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
    return FileVideo;
  }
  
  // Audio files
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
    return FileAudio;
  }
  
  // Code files
  if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json', 'xml', 'yaml', 'yml'].includes(extension)) {
    return FileCode;
  }
  
  // PowerPoint files
  if (['ppt', 'pptx', 'pptm', 'odp'].includes(extension)) {
    return File;
  }
  
  // Default
  return FileText;
};


function ReferencedFilesPanel({ selectedWorkspace, selectedFiles, onFileSelect, onFileDeselect }) {
  const { organization } = useOrganization();
  const clerk = useClerk();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const fileInputRef = React.useRef(null);

  // Load files when workspace changes
  useEffect(() => {
    if (organization && clerk?.session && selectedWorkspace?.workspace_id) {
      loadFiles();
    }
  }, [selectedWorkspace?.workspace_id, organization?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFiles = async () => {
    if (!organization || !clerk?.session) return;

    setIsLoading(true);
    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(`${apiBaseUrl}/references/?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load files: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setFiles(data.files || []);
      } else {
        console.error('Failed to load files:', data.detail);
      }
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!organization || !clerk?.session) return;

    setUploading(true);
    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${apiBaseUrl}/references/?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Reload files list
        await loadFiles();
      } else {
        console.error('Failed to upload file:', data.detail);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    if (!organization || !clerk?.session) return;

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(`${apiBaseUrl}/references/${fileId}?${queryParams}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Reload files list
        await loadFiles();
        // Deselect if this file was selected
        if (selectedFiles && selectedFiles.some(f => f.file_id === fileId) && onFileDeselect) {
          onFileDeselect(fileId);
        }
      } else {
        console.error('Failed to delete file:', data.detail);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    if (!organization || !clerk?.session) return;

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(`${apiBaseUrl}/references/${fileId}/download?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadFiles();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFileToggle = (file) => {
    if (isFileSelected(file.file_id)) {
      if (onFileDeselect) {
        onFileDeselect(file.file_id);
      }
    } else {
      if (onFileSelect) {
        onFileSelect(file);
      }
    }
  };

  const handlePreviewFile = async (file) => {
    if (!organization || !clerk?.session) return;

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      // Get the download URL for the file
      const downloadUrl = `${apiBaseUrl}/references/${file.file_id}/download?${queryParams}`;

      // Fetch with auth to get the actual file content URL
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load file: ${response.statusText}`);
      }

      // Create a blob URL from the response
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      setPreviewFile(file);
      setPreviewFileUrl(blobUrl);
    } catch (error) {
      console.error('Error loading file preview:', error);
    }
  };

  const closePreview = () => {
    // Clean up blob URL
    if (previewFileUrl) {
      window.URL.revokeObjectURL(previewFileUrl);
    }
    setPreviewFile(null);
    setPreviewFileUrl(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const isFileSelected = (fileId) => {
    return selectedFiles && selectedFiles.some(f => f.file_id === fileId);
  };

  return (
    <div className="referenced-files-panel">
      <div className="referenced-files-header">
        <div className="referenced-files-title">
          <span>FILES</span>
        </div>
        <div className="referenced-files-actions">
          <button
            className="referenced-files-edit"
            onClick={() => setShowManageModal(true)}
            disabled={!selectedWorkspace}
            title="Manage files"
          >
            <Pencil size={14} />
          </button>
          <button
            className="referenced-files-upload"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !selectedWorkspace}
            title="Upload file"
          >
            <Upload size={14} />
          </button>
          <button
            className="referenced-files-refresh"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            title="Refresh files"
          >
            <RefreshCw size={14} className={isRefreshing ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        accept="*/*"
      />

      <div className="referenced-files-list">
        {isLoading ? (
          <div className="referenced-files-loading">
            <div className="loading-spinner"></div>
            <span>Loading files...</span>
          </div>
        ) : files.length === 0 ? (
          <div className="referenced-files-empty">
            <FileText size={16} />
            <p>No reference files</p>
            <span>Upload files to use in context</span>
          </div>
        ) : (
          files.map((file) => {
            const FileIconComponent = getFileIcon(file.filename);
            const selected = isFileSelected(file.file_id);

            return (
            <div
              key={file.file_id}
              className={`referenced-file-item ${selected ? 'selected' : ''}`}
              onClick={() => handleFileToggle(file)}
              title={selected ? "Click to remove from scope" : "Click to include in scope"}
            >
              <div className="referenced-file-content">
                <div className="referenced-file-icon-name">
                  {selected && (
                    <div className="referenced-file-check">
                      <Check size={14} />
                    </div>
                  )}
                  <div className="referenced-file-icon">
                    <FileIconComponent size={16} />
                  </div>
                  <span className="referenced-file-name" title={file.filename}>
                    {file.filename}
                  </span>
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>

      {uploading && (
        <div className="referenced-files-uploading">
          <div className="loading-spinner"></div>
          <span>Uploading...</span>
        </div>
      )}
      {/* Manage Files Modal */}
      {showManageModal && (
        <div className="referenced-file-modal-overlay" onClick={() => setShowManageModal(false)}>
          <div className="referenced-file-modal manage-files-modal" onClick={(e) => e.stopPropagation()}>
            <div className="referenced-file-modal-header">
              <div className="referenced-file-modal-title">
                <Pencil size={18} />
                <span>Manage Files</span>
              </div>
              <button
                className="referenced-file-modal-close"
                onClick={() => setShowManageModal(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="manage-files-list">
              {files.length === 0 ? (
                <div className="manage-files-empty">
                  <FileText size={32} />
                  <p>No files uploaded yet</p>
                  <span>Upload files to provide context for your analysis</span>
                  <button
                    className="manage-files-upload-btn"
                    onClick={() => {
                      setShowManageModal(false);
                      fileInputRef.current?.click();
                    }}
                  >
                    <Upload size={16} />
                    Upload File
                  </button>
                </div>
              ) : (
                files.map((file) => {
                  const FileIconComponent = getFileIcon(file.filename);
                  return (
                    <div key={file.file_id} className="manage-file-item">
                      <div className="manage-file-info">
                        <FileIconComponent size={18} />
                        <div className="manage-file-details">
                          <span className="manage-file-name" title={file.filename}>{file.filename}</span>
                          <span className="manage-file-meta">{formatFileSize(file.file_size)} · {formatDate(file.uploaded_at)}</span>
                        </div>
                      </div>
                      <div className="manage-file-actions">
                        <button
                          className="manage-file-btn"
                          onClick={() => handlePreviewFile(file)}
                          title="Preview file"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="manage-file-btn"
                          onClick={() => handleDownloadFile(file.file_id, file.filename)}
                          title="Download file"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          className="manage-file-btn delete"
                          onClick={() => {
                            handleDeleteFile(file.file_id, file.filename);
                          }}
                          title="Delete file"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      <FileViewerModal
        isOpen={!!previewFile && !!previewFileUrl}
        onClose={closePreview}
        fileUrl={previewFileUrl}
        filename={previewFile?.filename}
        fileSize={previewFile?.file_size}
      />
    </div>
  );
}

export default ReferencedFilesPanel;

